import React, { createContext, useContext, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { ROLE_PERMISSIONS, ROLE_MODULE_ACCESS } from './roleBasedAccess';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const { accounts } = useMsal();

  const getUserRoles = useCallback(() => {
    // 1. Try getting roles from active account claims
    if (accounts && accounts.length > 0) {
      const claims = accounts[0].idTokenClaims;
      if (claims && Array.isArray(claims.roles)) {
        return claims.roles;
      }
    }
    
    // 2. Fallback to session storage (consistent with other parts of the app)
    try {
        const raw = sessionStorage.getItem('MSAL_LAST_IDTOKEN_CLAIMS');
        if (raw) {
            const claims = JSON.parse(raw);
            return Array.isArray(claims.roles) ? claims.roles : [];
        }
    } catch (e) {
        console.error("PermissionProvider: Failed to parse roles from session", e);
    }
    
    return [];
  }, [accounts]);

  const can = useCallback((action, module) => {
    const userRoles = getUserRoles();
    
    // Check if any of the user's roles allow this action on this module
    return userRoles.some(role => {
      // 1. Does this role have access to the module?
      const allowedModules = ROLE_MODULE_ACCESS[role];
      if (!allowedModules || !allowedModules.includes(module)) {
        return false;
      }

      // 2. Does this role have the specific permission?
      const permissions = ROLE_PERMISSIONS[role];
      if (!permissions || !permissions.includes(action)) {
        return false;
      }

      return true;
    });
  }, [getUserRoles]);

  return (
    <PermissionContext.Provider value={{ can }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};

export const Can = ({ perform, module, children, fallback = null }) => {
  const { can } = usePermission();
  
  if (can(perform, module)) {
    return <>{children}</>;
  }
  
  return <>{fallback}</>;
};
